/**
 * digitalcards backend client — Supabase queries against the `digitalcards.*` schema.
 *
 * Uses the existing `supabase` client with .schema('digitalcards') to access the
 * isolated schema. All returns are shaped per types.js.
 */
import { supabase } from '../../lib/supabase';
import { DEFAULT_CATALOG_SORT, SEARCH_RELEVANCE_SORT, filterList } from './catalogFilters';
import { recallCardArt, rememberCardArt } from './artMemory';
// Relevance for a search page: names that start with the word come first.
// Pure, and shared with the in-memory index so both rank the same way.
import { rankCards } from '../../utils/smartSearch';

/**
 * Everything a card grid needs from `catalog_browse`, in one list.
 *
 * The flat catalogue and the search results draw the same tiles from the same
 * view, so they read the same columns. Two lists drifting apart is how a
 * heading or a price ends up present on one screen and blank on the other.
 */
const CATALOG_COLUMNS = 'id, set_id, local_id, name, rarity, rarity_bucket, category, hp, '
  + 'types, primary_type, tcgdex_card_id, set_name, release_date, era_id, era_name, '
  + 'is_promo, is_pocket, near_mint';

/**
 * The most rows a facet read will pull back.
 *
 * The filter sheet fades the choices a result set has no cards for, so it has
 * to see the WHOLE result set and not the sixty rows on screen - fading Grass
 * because the first page happened to hold none would be a confident lie, the
 * same one collectionFacets.js exists to avoid. But an honest answer is not
 * worth an unbounded read from a phone: a two-letter query legitimately matches
 * thousands of names. Past this many rows the read reports itself CAPPED and
 * the sheet fades nothing at all, which is the only answer that is still true.
 */
export const FACET_ROW_CAP = 1200;

const SCHEMA = 'digitalcards';
const dc = () => supabase.schema(SCHEMA);

/** @returns {Promise<import('./types').Card | null>} */
export async function getCard(cardId, { includeVariants = false } = {}) {
  const { data: card, error } = await dc()
    .from('cards')
    .select('*, sets(name)')
    .eq('id', cardId)
    .maybeSingle();
  if (error) throw error;
  if (!card) return null;

  // Attach a primary image_url from the card's variants (prefer collectr, fall back to tcgdex).
  const { data: variants, error: vErr } = await dc()
    .from('card_variants')
    .select('*')
    .eq('card_id', cardId)
    .order('source', { ascending: false })  // collectr sorts before tcgdex alphabetically
    .limit(includeVariants ? 200 : 1);
  if (vErr) throw vErr;

  const primaryImage = variants && variants[0] ? variants[0].image_url : null;

  const { sets: setJoin, ...rest } = card;
  const result = {
    ...rest,
    set_name: setJoin?.name || null,
    image_url: primaryImage,
    _source: 'digitalcards',
  };
  if (includeVariants) {
    result.variants = variants || [];
  }
  return result;
}

/** @returns {Promise<import('./types').Card[]>} */
export async function searchCards(query, { limit = 50 } = {}) {
  if (!query || query.trim().length < 2) return [];

  const q = query.trim();
  const { data, error } = await dc()
    .from('cards')
    .select('id, set_id, local_id, name, rarity, category')
    .ilike('name', `%${q}%`)  // trigram index will accelerate this once created
    .order('name')
    .limit(limit);
  if (error) throw error;

  // Search results render as a grid — same burst risk as a set grid, so reuse
  // the same bulk/tcgdex-first image lookup instead of leaving image_url unset.
  const imageByCard = await getPrimaryImagesForCards((data || []).map((c) => c.id));

  return (data || []).map((c) => ({
    ...c,
    image_url: imageByCard[c.id] || null,
    _source: 'digitalcards',
  }));
}

/** @returns {Promise<import('./types').Set | null>} */
export async function getSet(setId, { includeCards = false } = {}) {
  const { data: set, error } = await dc()
    .from('sets')
    .select('*')
    .eq('id', setId)
    .maybeSingle();
  if (error) throw error;
  if (!set) return null;

  const result = { ...set, _source: 'digitalcards' };
  if (includeCards) {
    // The cards and their pictures in parallel. The pictures used to be looked
    // up by card id, so they waited for the card list and then pulled every
    // variant row of the set (1,072 for Base Set) to pick one each in the
    // browser. card_primary_image answers by set with one row per card.
    const [{ data: cards, error: cErr }, { data: images, error: iErr }] = await Promise.all([
      dc()
        .from('cards')
        .select('id, set_id, local_id, name, rarity, category, illustrator')
        .eq('set_id', setId)
        .order('local_id'),
      dc()
        .from('card_primary_image')
        .select('card_id, image_url')
        .eq('set_id', setId),
    ]);
    if (cErr) throw cErr;
    if (iErr) throw iErr;

    const imageByCard = {};
    for (const row of images || []) imageByCard[row.card_id] = row.image_url;
    result.cards = (cards || []).map((c) => ({
      ...c,
      image_url: imageByCard[c.id] || null,
      _source: 'digitalcards',
    }));
  }
  return result;
}

/**
 * Source preference for a card's primary image in BULK (whole-set-grid) fetches: lower rank wins.
 * tcgdex first here, not collectr — Collectr's CDN 503s when a set grid fires ~100+ concurrent
 * image requests at once (verified: same URLs succeed individually, fail under burst from this
 * app). TCGdex's CDN has no such limit for concurrent card/logo images. Single-card lookups
 * (getCard, one request at a time) are unaffected and keep their own ordering.
 */
const SOURCE_RANK = { tcgdex: 0, collectr: 1 };
const sourceRank = (s) => (s in SOURCE_RANK ? SOURCE_RANK[s] : 2);

/**
 * Bulk lookup: one primary image_url per card id, from card_variants.
 * @param {string[]} cardIds
 * @returns {Promise<Record<string, string>>} map of card_id → image_url
 */
async function getPrimaryImagesForCards(cardIds) {
  const imageByCard = {};
  const rankByCard = {};
  const CHUNK = 200; // keep PostgREST .in() URLs a sane length

  // Chunks used to run one after another, so a 295-card set paid two round
  // trips where one would do. They do not depend on each other.
  const chunks = [];
  for (let i = 0; i < cardIds.length; i += CHUNK) chunks.push(cardIds.slice(i, i + CHUNK));

  const results = await Promise.all(chunks.map((chunk) => dc()
    .from('card_variants')
    .select('card_id, image_url, source')
    .in('card_id', chunk)
    .not('image_url', 'is', null)));

  for (const { data: variants, error } of results) {
    if (error) throw error;
    for (const v of variants || []) {
      const rank = sourceRank(v.source);
      if (!(v.card_id in imageByCard) || rank < rankByCard[v.card_id]) {
        imageByCard[v.card_id] = v.image_url;
        rankByCard[v.card_id] = rank;
      }
    }
  }
  return imageByCard;
}

/**
 * One primary image URL per card id, for a handful of specific cards.
 *
 * The fan on the All Cards tile names five cards by id and needs their pictures.
 * Ids are stable; the image behind one is content-hashed and can be re-mirrored,
 * so the tile asks by id rather than carrying five frozen URLs that would
 * silently rot.
 *
 * @param {string[]} cardIds
 * @returns {Promise<Record<string, string>>}
 */
export async function getCardImages(cardIds) {
  if (!cardIds || cardIds.length === 0) return {};

  // Whatever this device already knows is answered without a round trip, and
  // only the rest is asked for. The ranked ten is the caller this matters to:
  // it cannot paint a tile until this resolves, so on a revisit the difference
  // is between ten instant cards and ten black squares.
  const { found, missing } = recallCardArt(cardIds);
  if (missing.length === 0) return found;

  const fresh = await getPrimaryImagesForCards(missing);
  rememberCardArt(fresh);
  return { ...found, ...fresh };
}

/**
 * The market price of a named handful of catalogue cards.
 *
 * For screens that hold card IDS rather than catalogue rows - a chase binder's
 * stored wish rows are the case this was written for. The alternative was a
 * price column on `wish_binder_cards`, which would be a copy of a number this
 * app already refreshes on its own schedule: stale the day after it is written,
 * and wrong in the one direction that matters, since a chase list is exactly
 * where somebody is watching a price move.
 *
 * Reads the same `near_mint` column `listCatalogCards` puts on a tile, so a
 * card cannot quote one price in the picker and another inside the binder.
 *
 * @param {string[]} cardIds
 * @returns {Promise<Record<string, number|null>>}
 */
export async function getCatalogPrices(cardIds) {
  const ids = [...new Set((cardIds || []).filter(Boolean))];
  const out = {};
  for (let i = 0; i < ids.length; i += 200) {
    const chunk = ids.slice(i, i + 200);
    const { data, error } = await dc()
      .from('catalog_browse')
      .select('id, near_mint')
      .in('id', chunk);
    if (error) throw error;
    for (const row of data || []) out[row.id] = row.near_mint ?? null;
  }
  return out;
}

/** @returns {Promise<import('./types').Set[]>} */
export async function getSets({ eraId = null } = {}) {
  // sets_with_counts is sets plus how many cards and printings we actually hold
  // for each, so a tile can say "408 printings" without a second read.
  let q = dc()
    .from('sets_with_counts')
    .select('id, name, era_id, abbreviation, release_date, card_count_total, card_count_official, is_promo_line, is_master_subset, parent_set_id, display_order, logo_url, provisional_source, card_count, printing_count')
    .order('release_date', { ascending: false, nullsFirst: false });
  if (eraId) q = q.eq('era_id', eraId);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map((s) => ({ ...s, _source: 'digitalcards' }));
}

/** @returns {Promise<import('./types').Variant[]>} */
export async function getCardVariants(cardId) {
  const { data, error } = await dc()
    .from('card_variants')
    .select('*')
    .eq('card_id', cardId)
    .order('source');
  if (error) throw error;
  return data || [];
}

/**
 * Return a variant-specific image URL when variantId is given, else the card's primary image.
 * @returns {Promise<string | null>}
 */
export async function getCardImage(cardId, variantId = null) {
  if (variantId) {
    const { data, error } = await dc()
      .from('card_variants')
      .select('image_url')
      .eq('id', variantId)
      .maybeSingle();
    if (error) throw error;
    return data ? data.image_url : null;
  }
  const card = await getCard(cardId);
  return card ? card.image_url : null;
}

/** @returns {Promise<import('./types').Era[]>} */
export async function getEras() {
  const { data, error } = await dc()
    .from('eras')
    .select('*')
    .order('display_order');
  if (error) throw error;
  return data || [];
}

/**
 * Sort facts for a batch of catalogue ids: hp, types, set and release date.
 * One request per 200 ids. Ids with no catalogue row are simply absent.
 * @param {string[]} cardIds
 * @returns {Promise<Record<string, {hp:number|null, types:string[]|null, setId:string|null, setName:string|null, releaseDate:string|null, localId:string|null}>>}
 */
export async function getCardMeta(cardIds) {
  const ids = [...new Set((cardIds || []).filter(Boolean))];
  const out = {};
  for (let i = 0; i < ids.length; i += 200) {
    const chunk = ids.slice(i, i + 200);
    const { data, error } = await dc()
      .from('cards')
      .select('id, hp, types, rarity, set_id, local_id, sets(name, release_date, era_id)')
      .in('id', chunk);
    if (error) throw error;
    for (const row of data || []) {
      out[row.id] = {
        hp: row.hp ?? null,
        types: row.types ?? null,
        // Rarity and era so the All tab and binders can offer the same
        // orders the catalogue does. A saved collection row carries neither.
        rarity: row.rarity ?? null,
        setId: row.set_id ?? null,
        setName: row.sets?.name ?? null,
        releaseDate: row.sets?.release_date ?? null,
        eraId: row.sets?.era_id ?? null,
        localId: row.local_id ?? null,
      };
    }
  }
  return out;
}

/**
 * Smart catalogue card search, run against our own 23,591 rows.
 *
 * Takes the parsed shape from utils/smartSearch rather than a string, so the
 * tokenising rules live in one place and every caller gets the same ones.
 *
 * Reads `catalog_browse` rather than `cards`. Same rows, same count, same
 * indexes by name and number — and it already carries the set name, the
 * release date and the market price, so a search result can show what a card
 * is worth without a second round trip per row. The owner asked for the price
 * to be there "especially in the search bars"; this is what makes that free.
 *
 * Index strategy:
 *   - word tokens  → one chained .ilike('name', '%w%') each. AND-ed by
 *     PostgREST, and served by the GIN trigram index catalog_browse_name_trgm_idx.
 *   - number tokens → .in('local_id', [...padded and unpadded forms]).
 *   - both → the planner picks whichever is narrower, which for "char 004" is
 *     the number.
 *
 * Everything the caller needs to tell two printings apart — set name, number,
 * release date, price and art — comes back in two requests total, not one per
 * row.
 *
 * SORT AND FILTER, and why they belong here rather than over the rows.
 *
 * The owner's complaint was that a search result was a dead end: "it should
 * have my 3 layers of zoom. and SORT. but a bit dynamic for the card im
 * searching." Re-ordering the sixty rows already fetched would order the page
 * and lie about the rest of the match, which is exactly the reason the flat
 * catalogue's sort lives in the database - so this one takes the SAME two
 * helpers, `applyCatalogFilters` and `applyCatalogSort`. One ordering
 * implementation, one filtering implementation, shared by both screens.
 *
 * Relevance is the one order that cannot be expressed as an ORDER BY, because
 * it is the two-pass structure below rather than a column. So an explicit sort
 * takes the single contains pass and the database's order, and relevance keeps
 * the two passes. Sort is king: when a person asks for value high to low they
 * get the sixty most valuable cards in the whole match, not the sixty most
 * valuable of whichever cards relevance happened to surface first.
 *
 * @param {import('../../utils/smartSearch').ParsedQuery} parsed
 * @param {{
 *   limit?: number, setIds?: string[]|null, words?: string[],
 *   sort?: string|null, filters?: object|null,
 * }} [options]
 */
export async function searchCatalogCards(parsed, {
  limit = 60, setIds = null, words = null,
  sort = null, filters = null,
} = {}) {
  const terms = words || parsed?.words || [];
  const localIds = parsed?.localIds || [];
  if (!terms.length && !localIds.length) return [];

  const ordered = Boolean(sort) && sort !== SEARCH_RELEVANCE_SORT;

  // Two passes, in parallel, because a LIMIT has to cut somewhere and
  // alphabetical is the wrong place to cut. "char" matches 305 cards; the first
  // 60 by name are Blaine's Charmander and Big Charm, and Charizard never
  // appears. The prefix pass answers what the person meant, the contains pass
  // fills the rest, and both ride the same trigram index.
  // One pass, not two. The prefix pass used to run beside the contains pass
  // so that "char" put Charizard ahead of Blaine's Charmander. It cost a
  // second round trip on every keystroke, which on a phone with two bars is
  // the difference between a search and a wait. The same guarantee now comes
  // from asking for a deeper page and ranking it here: every name that
  // STARTS with the word sorts under its own first letter, so a page four
  // times the limit holds all of them, and rankCards puts them first.
  //
  // Under an explicit order the page is the limit and nothing is re-ranked:
  // the database's order is the whole point of the sort.
  const cap = terms.length && !ordered ? Math.min(limit * 4, 240) : limit;
  let q = dc().from('catalog_browse').select(CATALOG_COLUMNS);
  terms.forEach((w) => { q = q.ilike('name', `%${w}%`); });
  if (localIds.length) q = q.in('local_id', localIds);
  if (setIds && setIds.length) q = q.in('set_id', setIds);
  q = applyCatalogFilters(q, filters);
  q = ordered ? applyCatalogSort(q, sort) : q.order('sort_name');
  const { data, error } = await q.limit(cap);
  if (error) throw error;

  const merged = (ordered ? (data || []) : rankCards(parsed, data || [])).slice(0, limit);
  if (!merged.length) return [];

  const imageByCard = await getPrimaryImagesForCards(merged.map((c) => c.id));

  // set_name and release_date arrive on the row now, so nothing is unwrapped.
  return merged.map((c) => ({
    ...c,
    image_url: imageByCard[c.id] || null,
    _source: 'digitalcards',
  }));
}

/**
 * One page of the whole catalogue, in whatever order and under whatever filter
 * the browser asked for.
 *
 * 23,591 rows is far too many to hold in the browser, so this is a real
 * server-side page — .range(), the way the collection's own first-page query
 * works — not a display limit over a fetched-everything array. Which means the
 * ORDER has to be server-side too. Sorting the sixty rows already fetched puts
 * the page in order and lies about the other 23,531, and that is exactly why
 * this screen shipped with no sort control at all until now.
 *
 * Everything is read from `digitalcards.catalog_browse`, a materialized view
 * that flattens the card, its set, its era and its market price into one
 * indexed row. Three of the orders offered here cannot be answered from
 * `digitalcards.cards`: value at all, because prices live in `public.card_prices`
 * across a lowercased text join measured at 3,166ms, and the two set-date
 * orders only by joining `sets` and sorting the whole catalogue on every page.
 *
 * Every row carries its set name, because alphabetical order puts twenty
 * Charizards in a row and without the set they are indistinguishable.
 *
 * `setId` narrows the same read to one set, which is how a screen can drill
 * into a set and still get the price, the era and the set name that the flat
 * catalogue's tiles carry. `digitalcards.cards` could answer the rows but not
 * the price, and a set grid quoting no money where the catalogue quotes some
 * would be the same card priced two ways on two screens.
 *
 * `ids` narrows it to a named list of cards, which is what a chase binder is:
 * an arbitrary handful from anywhere in the catalogue, in whatever order and
 * under whatever filter the person chose. It goes through this function rather
 * than a hand-rolled read of its own so that the order and the filtering are
 * still the database's, and a binder cannot sort by value in a way that
 * disagrees with how Explore sorts by value. One request carries the whole list:
 * 800 ids is a 6 KB URL, which PostgREST answers without complaint, and no
 * chunking is used because a list split across requests can only be ordered
 * within each request.
 *
 * @param {{
 *   from?: number, to?: number, withCount?: boolean,
 *   sort?: string, filters?: object, setId?: string|null, ids?: string[]|null,
 * }} [options]
 * @returns {Promise<{ rows: object[], total: number|null }>}
 */
export async function listCatalogCards({
  from = 0, to = 59, withCount = false,
  sort = DEFAULT_CATALOG_SORT, filters = null, setId = null, ids = null,
} = {}) {
  // An empty id list is a real answer - a chase binder with nothing in it - and
  // must not be read as "no narrowing" and return the whole catalogue.
  if (ids && ids.length === 0) return { rows: [], total: withCount ? 0 : null };

  let q = dc()
    .from('catalog_browse')
    .select(CATALOG_COLUMNS, withCount ? { count: 'exact' } : undefined);

  if (setId) q = q.eq('set_id', setId);
  if (ids) q = q.in('id', ids);
  q = applyCatalogFilters(q, filters);
  q = applyCatalogSort(q, sort);

  const { data, error, count } = await q.range(from, to);
  if (error) throw error;

  const rows = data || [];
  const imageByCard = await getPrimaryImagesForCards(rows.map((c) => c.id));

  return {
    rows: rows.map((c) => ({
      ...c,
      image_url: imageByCard[c.id] || null,
      _source: 'digitalcards',
    })),
    total: withCount ? (count ?? null) : null,
  };
}

/**
 * Which filter choices a search result actually contains.
 *
 * This is the "a bit dynamic for the card im searching" half of the job.
 * Searching "magikarp" must offer the rarities, sets, eras and types those
 * Magikarps really have, and fade the rest - the owner asked for the same
 * behaviour the All tab already has: "dynamic enough to fade out filters that
 * aren't in your card collection."
 *
 * THREE THINGS THIS DELIBERATELY GETS RIGHT.
 *
 * It reads the whole match, not the page. The rows on screen are sixty of a
 * possible three hundred, and a facet drawn from sixty would fade choices the
 * result set genuinely holds. So this is its own narrow read - six columns, no
 * ordering, no images - rather than a pass over `cardHits`.
 *
 * It reads the match UNFILTERED. The facets describe what the QUERY matches,
 * never what the current ticks leave behind. Computed after filtering, ticking
 * Grass would fade Fire, and there would then be no way back to Fire except
 * unticking Grass first - a filter sheet that traps you in its own first
 * choice.
 *
 * It admits when it does not know. Past FACET_ROW_CAP rows `capped` comes back
 * true and the caller must fade nothing, for the same reason a null collection
 * facet fades nothing: "I have not counted this" and "you own none of these"
 * must never look the same on screen.
 *
 * @param {{ words?: string[], localIds?: string[], setIds?: string[]|null }} match
 *   the predicate that actually answered - the winning tier from
 *   smartCatalogSearch, so the facets and the rows can never describe two
 *   different queries.
 * @returns {Promise<{
 *   types: string[], rarities: string[], eras: string[], kinds: string[],
 *   show: string[], sets: string[], total: number, capped: boolean
 * }|null>} null when the read failed, which fades nothing.
 */
export async function searchCatalogFacets({ words = [], localIds = [], setIds = null } = {}) {
  if (!words.length && !localIds.length) return null;

  let q = dc()
    .from('catalog_browse')
    .select('rarity_bucket, types, era_id, set_id, category, is_promo, is_pocket');
  words.forEach((w) => { q = q.ilike('name', `%${w}%`); });
  if (localIds.length) q = q.in('local_id', localIds);
  if (setIds && setIds.length) q = q.in('set_id', setIds);

  // One over the cap, so a result set sitting exactly on it is not reported as
  // capped when it was in fact counted completely.
  const { data, error } = await q.limit(FACET_ROW_CAP + 1);
  if (error) {
    console.warn('[search] facet read failed:', error.message);
    return null;
  }

  const rows = data || [];
  const capped = rows.length > FACET_ROW_CAP;

  const types = new Set();
  const rarities = new Set();
  const eras = new Set();
  const kinds = new Set();
  const sets = new Set();
  const show = new Set();
  for (const row of rows) {
    for (const t of row.types || []) if (t) types.add(t);
    if (row.rarity_bucket) rarities.add(row.rarity_bucket);
    if (row.era_id) eras.add(row.era_id);
    if (row.category) kinds.add(row.category);
    if (row.set_id) sets.add(row.set_id);
    if (row.is_promo) show.add('promos');
    if (row.is_pocket) show.add('free');
  }

  return {
    types: [...types],
    rarities: [...rarities],
    eras: [...eras],
    kinds: [...kinds],
    sets: [...sets],
    total: rows.length,
    capped,
  };
}

/**
 * How many cards a filter actually matches, without fetching any of them.
 *
 * The loading line names a number — "Sorting 2,486 Ultra Rare cards by value" —
 * and a number nobody counted is how a screen starts lying. This counts.
 */
export async function countCatalogCards({ filters = null } = {}) {
  let q = dc()
    .from('catalog_browse')
    .select('id', { count: 'exact', head: true });
  q = applyCatalogFilters(q, filters);
  const { count, error } = await q;
  if (error) throw error;
  return count ?? null;
}

/**
 * Narrow a catalog_browse query to the chosen filters.
 *
 * Every group is multi-select and every group is an OR within itself and an AND
 * between: Grass OR Lightning, AND Trainer OR Energy. That is what a person
 * means when they tick several things, and it is what keeps a child's five
 * random ticks to one well-defined, smaller list rather than nothing at all.
 *
 * An empty group does not narrow. There is no "all" value to select, because
 * "all" is what having chosen nothing already means.
 */
function applyCatalogFilters(query, filters) {
  if (!filters) return query;
  let q = query;

  // Promos and Free are two independent inclusions, so ticking both means
  // either, not both at once - no card is a promo AND a Pocket card.
  const show = filterList(filters, 'show');
  if (show.length === 1) {
    q = show[0] === 'free' ? q.eq('is_pocket', true) : q.eq('is_promo', true);
  } else if (show.length > 1) {
    q = q.or('is_promo.eq.true,is_pocket.eq.true');
  }

  const kinds = filterList(filters, 'kinds');
  if (kinds.length) q = q.in('category', kinds);

  // `overlaps`, not `contains`: a card matches if it carries ANY of the chosen
  // types. `contains` would demand a card be Grass AND Lightning at once, which
  // is a handful of cards rather than the two thousand the person expects.
  const types = filterList(filters, 'types');
  if (types.length) q = q.overlaps('types', types);

  const eras = filterList(filters, 'eras');
  if (eras.length) q = q.in('era_id', eras);

  // Rarity arrives as buckets - "ultra", "special-illustration" - and expands
  // to every printed string that means each, Pocket's own ladder included.
  // "Unlisted" is the 883 cards with no rarity recorded; it has no strings to
  // expand to, so it is asked for as a null instead of being silently dropped.
  // Matched on the bucket column, not on the rarity strings behind it.
  //
  // Every real rung could be expanded to its strings, but the two at the bottom
  // cannot: Promo and Decks & Kits are defined by the SET a card came from,
  // because a promo and a starter-deck card both have no rarity symbol printed
  // on them and the rarity column cannot tell them apart. The database works
  // the bucket out once, at refresh, and both kinds match the same way here.
  const rarities = filterList(filters, 'rarities');
  if (rarities.length) q = q.in('rarity_bucket', rarities);

  return q;
}

/**
 * Put a catalog_browse query in order.
 *
 * Every order ends on `id` so a card can never straddle two pages: without a
 * unique tiebreaker two cards with the same HP can swap places between the
 * request for rows 0-59 and the request for rows 60-119, and one of them is
 * then shown twice while the other is never shown at all.
 *
 * NATURAL CARD ORDER, and where it applies. The owner's resting state is that
 * "cards are naturally in order by their card numbers", and that is the tail of
 * every order which produces GROUPS - set, era, rarity, type. Inside a group
 * those orders say nothing about sequence on their own, so card number fills
 * the silence, which is what a collector expects when they open a set.
 *
 * It does NOT apply to value, HP or name, and that is the answer to where the
 * two ideas collide. Those three define the sequence all the way down by
 * themselves: a value order that reverted to card number inside some invented
 * price band would no longer be a value order, and an A-Z that ran by card
 * number inside each letter would no longer be alphabetical. Sort is king, so
 * the sort wins; card number is what fills a gap the sort leaves, never
 * something that overrides it. Those three orders also carry no separators, for
 * the same underlying reason - there is no group for card number to be "inside"
 * of.
 *
 * `nullsFirst: false` on value and HP is honest rather than cosmetic. A card
 * with no price is not the cheapest card in the catalogue, it is a card nobody
 * has priced, and a free TCG Pocket card is not worth nothing either. Both sink
 * to the end whichever way the list is read, and neither is ever ranked at zero.
 */
function applyCatalogSort(query, sort) {
  // set, then the printed card number split into its letter prefix and its
  // digits, so a set's main run (001-198) comes before its H, SV and TG
  // subsets instead of interleaving with them alphabetically.
  const natural = (q) => q.order('set_id').order('local_prefix')
    .order('local_num', { nullsFirst: false }).order('local_id');

  switch (sort) {
    case 'value-desc':
      return query.order('near_mint', { ascending: false, nullsFirst: false }).order('id');
    case 'value-asc':
      return query.order('near_mint', { ascending: true, nullsFirst: false }).order('id');
    case 'name-desc':
      return query.order('sort_name', { ascending: false }).order('id');
    // `set_id` sits directly under the date on purpose. Sets genuinely share
    // release days - Mega Evolution and MEP Black Star Promos are both
    // 2025-09-26 - so ordering by date alone interleaves two sets' cards, and
    // on a grid with set headers that reads as two headings alternating down
    // the screen.
    case 'set-newest':
      return natural(query.order('release_date', { ascending: false, nullsFirst: false }));
    case 'set-oldest':
      return natural(query.order('release_date', { ascending: true, nullsFirst: false }));
    case 'era-newest':
      return natural(query.order('era_order', { ascending: false, nullsFirst: false }));
    case 'era-oldest':
      return natural(query.order('era_order', { ascending: true, nullsFirst: false }));
    // NULLS LAST on BOTH directions. Promo and unrecorded rarities are not
    // rungs on the ladder, so they sink whichever way it is read - the same
    // treatment as a card with no price. Without this, descending led with 883
    // Unlisted cards, because a descending btree defaults to NULLS FIRST.
    case 'rarity-desc':
      return natural(query.order('rarity_rank', { ascending: false, nullsFirst: false }));
    case 'rarity-asc':
      return natural(query.order('rarity_rank', { ascending: true, nullsFirst: false }));
    case 'type':
      return natural(query.order('primary_type', { nullsFirst: false }));
    case 'hp-desc':
      return query.order('hp', { ascending: false, nullsFirst: false }).order('id');
    case 'hp-asc':
      return query.order('hp', { ascending: true, nullsFirst: false }).order('id');
    case 'name-asc':
    default:
      return query.order('sort_name').order('id');
  }
}
